import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtTime } from "@/components/PageHeader";
import { shortNo } from "@/lib/caseNo";
import { STICKY_CELL } from "@/components/review/SubmissionReviewCard";

export const caseColumns = (withMember) => ["Case #", ...(withMember ? ["Member"] : []), "Policy", "Event Date", "Status", "Action"];

export const CASE_FILTERS = [["open", "Awaiting/Conference"], ["repealed", "Repealed"], ["nfa", "No Further Action"], ["all", "All"]];
export const CASE_GROUPS = { open: ["no_action_yet", "conference_requested", "conference_date"], repealed: ["rescinded"], nfa: ["no_further_action_needed"] };
export const CASE_EMPTY = {
  open: "No cases are awaiting acknowledgement or a conference.",
  repealed: "No repealed cases.",
  nfa: "No cases marked No Further Action.",
  all: "No merit or demerit cases yet.",
};

const confWhen = (c) => `${fmtDate(c.conference_date)}${c.conference_time ? ` at ${fmtTime(c.conference_time)}` : ""}`;
const stop = (e) => e.stopPropagation();

function CaseDetails({ c, span, confTestId }) {
  return (
    <TableRow className="bg-slate-50/70 hover:bg-slate-50/70" data-testid={`case-details-${c.case_number}`}>
      <TableCell colSpan={span} className="px-6 py-3">
        <div className="sticky left-6 max-w-[min(48rem,calc(100vw-5rem))]">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Description</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700" data-testid={`case-description-${c.case_number}`}>{c.description || "No description."}</p>
          {c.preset_name && <p className="mt-2 text-xs text-slate-500">Policy: {c.preset_name}</p>}
          {c.conference_date && <p className="mt-1 text-xs font-semibold text-blue-600" data-testid={confTestId}>Conference Scheduled for {confWhen(c)}</p>}
        </div>
      </TableCell>
    </TableRow>
  );
}

// One merit case on a single line (Status = type + value, Action = where it stands); tap to show the description.
// compact: conference date moves to a tooltip + the details row (for narrow containers like the Member Summary).
export const MeritCaseRow = React.forwardRef(function MeritCaseRow(
  { c, withMember, confTestId, actionExtra, buttons, compact = false, defaultOpen = false, className = "", testId }, ref) {
  const [open, setOpen] = useState(defaultOpen);
  const span = caseColumns(withMember).length + (buttons !== undefined ? 1 : 0);
  return (
    <>
      <TableRow ref={ref} onClick={() => setOpen((o) => !o)} aria-expanded={open} className={`cursor-pointer ${className}`} data-testid={testId}>
        <TableCell className="whitespace-nowrap font-mono text-xs font-semibold" data-testid={`case-no-${c.case_number}`}>
          <span className="inline-flex items-center gap-1">
            <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />{shortNo(c.case_number)}
          </span>
        </TableCell>
        {withMember && <TableCell className={`whitespace-nowrap text-sm font-medium ${STICKY_CELL}`}>{c.team_member_name}</TableCell>}
        <TableCell className="max-w-[14rem] truncate text-sm text-slate-700" title={c.preset_name || "Other"} data-testid={`case-policy-${c.case_number}`}>
          {c.preset_name || <span className="text-slate-400">Other</span>}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm">{fmtDate(c.date_issued)}</TableCell>
        <TableCell className="whitespace-nowrap" data-testid={`case-status-${c.case_number}`}>
          <StatusBadge status={c.case_type} />
          <span className={`ml-1 text-xs font-bold tabular-nums ${c.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{c.value > 0 ? `+${c.value}` : c.value}</span>
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid={`case-action-${c.case_number}`}>
          <span className="inline-flex items-center gap-1.5" title={c.conference_date ? `Conference Scheduled for ${confWhen(c)}` : undefined}>
            <StatusBadge status={c.status} />
            {c.conference_date && !compact && (
              <span className="text-xs font-semibold text-blue-600" data-testid={`${confTestId}-${c.case_number}`}>{confWhen(c)}</span>
            )}
            {actionExtra && <span onClick={stop}>{actionExtra}</span>}
          </span>
        </TableCell>
        {buttons !== undefined && <TableCell className="whitespace-nowrap" onClick={stop}>{buttons}</TableCell>}
      </TableRow>
      {open && <CaseDetails c={c} span={span} confTestId={compact ? `${confTestId}-${c.case_number}` : undefined} />}
    </>
  );
});
